import React, { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CreditCard, Mail, MessageSquareText, DollarSign, CheckCircle2, AlertCircle } from "lucide-react";

function StatusPill({ ok, label }) {
  return (
    <span data-testid="integration-status" className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${ok ? "bg-[#E3F3ED] text-[#1A6E4E]" : "bg-[#FBEAEA] text-[#9B2C2C]"}`}>
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />} {label}
    </span>
  );
}

export function IntegrationsManager() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/config");
      setCfg(data);
      setForm({
        price_alr_small: data.pricing.price_alr_small,
        price_alr_mid: data.pricing.price_alr_mid,
        price_alr_large: data.pricing.price_alr_large,
        price_nursing: data.pricing.price_nursing,
        price_private: data.pricing.price_private,
        concierge_fee: data.pricing.concierge_fee,
        annual_enabled: data.pricing.annual_enabled,
        annual_months: data.pricing.annual_months,
        stripe_enabled: data.integrations.stripe.enabled,
        resend_enabled: data.integrations.resend.enabled,
        twilio_enabled: data.integrations.twilio.enabled,
        sender_email: data.integrations.resend.sender_email || "",
        twilio_from_number: data.integrations.twilio.from_number || "",
        stripe_api_key: "", resend_api_key: "", twilio_account_sid: "", twilio_auth_token: "",
      });
    } catch { toast.error("Could not load settings"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/config", form);
      setCfg(data);
      setForm((p) => ({ ...p, stripe_api_key: "", resend_api_key: "", twilio_account_sid: "", twilio_auth_token: "" }));
      toast.success("Settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  if (loading || !cfg) return <div className="py-16 text-center text-[#5C6660]"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  const card = "bg-white border border-[#E8E6DF] rounded-2xl p-6";

  return (
    <div className="space-y-6" data-testid="integrations-manager">
      {/* Pricing */}
      <div className={card}>
        <div className="flex items-center gap-2 mb-4"><DollarSign className="h-5 w-5 text-[#0077B6]" /><h3 className="font-heading text-lg font-medium">Premium pricing (by facility type & size)</h3></div>
        <p className="text-sm text-[#5C6660] mb-4">Each facility's monthly Premium price is computed from its type & licensed bed count. These amounts drive checkout and the For-Facilities page instantly.</p>
        <div className="space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#0077B6] mb-2">Assisted Living (ALR / ALR·ACF / ALR·BISL)</p>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-1.5"><Label htmlFor="price-alr-small">Up to 15 beds ($)</Label><Input id="price-alr-small" type="number" min="0" step="1" data-testid="price-alr-small" value={form.price_alr_small} onChange={(e) => set("price_alr_small", e.target.value)} className="rounded-xl" /></div>
              <div className="space-y-1.5"><Label htmlFor="price-alr-mid">16–74 beds ($)</Label><Input id="price-alr-mid" type="number" min="0" step="1" data-testid="price-alr-mid" value={form.price_alr_mid} onChange={(e) => set("price_alr_mid", e.target.value)} className="rounded-xl" /></div>
              <div className="space-y-1.5"><Label htmlFor="price-alr-large">75+ beds ($)</Label><Input id="price-alr-large" type="number" min="0" step="1" data-testid="price-alr-large" value={form.price_alr_large} onChange={(e) => set("price_alr_large", e.target.value)} className="rounded-xl" /></div>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label htmlFor="price-nursing">Nursing (NF/SNF) ($)</Label><Input id="price-nursing" type="number" min="0" step="1" data-testid="price-nursing" value={form.price_nursing} onChange={(e) => set("price_nursing", e.target.value)} className="rounded-xl" /></div>
            <div className="space-y-1.5"><Label htmlFor="price-private">Private Nursing Facility ($)</Label><Input id="price-private" type="number" min="0" step="1" data-testid="price-private" value={form.price_private} onChange={(e) => set("price_private", e.target.value)} className="rounded-xl" /></div>
            <div className="space-y-1.5"><Label htmlFor="price-concierge">Concierge fee ($)</Label><Input id="price-concierge" type="number" min="0" step="1" data-testid="price-concierge" value={form.concierge_fee} onChange={(e) => set("concierge_fee", e.target.value)} className="rounded-xl" /></div>
          </div>
          <div className="border-t border-[#F2F1EC] pt-4">
            <div className="flex items-center justify-between gap-3">
              <div><div className="text-sm font-medium text-[#1C211F]">Annual plan — "pay for {form.annual_months || 10}, get 12"</div><div className="text-xs text-[#5C6660]">Let facilities pay yearly at a discount ({12 - (form.annual_months || 10)} months free).</div></div>
              <Switch data-testid="toggle-annual" checked={!!form.annual_enabled} onCheckedChange={(v) => set("annual_enabled", v)} />
            </div>
            {form.annual_enabled && (
              <div className="mt-3 max-w-[220px] space-y-1.5">
                <Label htmlFor="annual-months">Months charged per year</Label><Input id="annual-months" type="number" min="1" max="12" step="1" data-testid="annual-months" value={form.annual_months} onChange={(e) => set("annual_months", e.target.value)} className="rounded-xl" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stripe */}
      <div className={card}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-[#0077B6]" /><h3 className="font-heading text-lg font-medium">Stripe — Payments</h3></div>
          <div className="flex items-center gap-3">
            <StatusPill ok={cfg.integrations.stripe.configured} label={cfg.integrations.stripe.configured ? `${cfg.integrations.stripe.mode} key` : "Not configured"} />
            <Switch data-testid="toggle-stripe" checked={form.stripe_enabled} onCheckedChange={(v) => set("stripe_enabled", v)} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="stripe-key">Secret API key {cfg.integrations.stripe.configured && <span className="text-[#5C6660] font-normal">(current: {cfg.integrations.stripe.hint})</span>}</Label><Input id="stripe-key" type="password" placeholder="sk_live_… (leave blank to keep current)" data-testid="stripe-key" value={form.stripe_api_key} onChange={(e) => set("stripe_api_key", e.target.value)} className="rounded-xl font-mono text-sm" />
          <p className="text-xs text-[#5C6660]">Paste your live key here when you're ready to switch from test to live.</p>
        </div>
      </div>

      {/* Resend */}
      <div className={card}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><Mail className="h-5 w-5 text-[#0077B6]" /><h3 className="font-heading text-lg font-medium">Resend — Email</h3></div>
          <div className="flex items-center gap-3">
            <StatusPill ok={cfg.integrations.resend.configured} label={cfg.integrations.resend.configured ? "Connected" : "Not configured"} />
            <Switch data-testid="toggle-resend" checked={form.resend_enabled} onCheckedChange={(v) => set("resend_enabled", v)} />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="resend-key">API key {cfg.integrations.resend.configured && <span className="text-[#5C6660] font-normal">(set)</span>}</Label><Input id="resend-key" type="password" placeholder="re_… (leave blank to keep current)" data-testid="resend-key" value={form.resend_api_key} onChange={(e) => set("resend_api_key", e.target.value)} className="rounded-xl font-mono text-sm" />
          </div>
          <div className="space-y-1.5"><Label htmlFor="resend-sender">Sender (from)</Label><Input id="resend-sender" data-testid="resend-sender" value={form.sender_email} onChange={(e) => set("sender_email", e.target.value)} className="rounded-xl text-sm" placeholder="CareBed <notifications@yourdomain.com>" /></div>
        </div>
      </div>

      {/* Twilio */}
      <div className={card}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><MessageSquareText className="h-5 w-5 text-[#0077B6]" /><h3 className="font-heading text-lg font-medium">Twilio — SMS</h3></div>
          <div className="flex items-center gap-3">
            <StatusPill ok={cfg.integrations.twilio.configured} label={cfg.integrations.twilio.configured ? "Connected" : "Not configured"} />
            <Switch data-testid="toggle-twilio" checked={form.twilio_enabled} onCheckedChange={(v) => set("twilio_enabled", v)} />
          </div>
        </div>
        <p className="text-sm text-[#5C6660] mb-4">When off (or keys missing), bed-count SMS requests are logged as mock and never actually sent.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label htmlFor="twilio-sid">Account SID {cfg.integrations.twilio.account_sid_hint && <span className="text-[#5C6660] font-normal">({cfg.integrations.twilio.account_sid_hint})</span>}</Label><Input id="twilio-sid" type="password" placeholder="AC… (leave blank to keep)" data-testid="twilio-sid" value={form.twilio_account_sid} onChange={(e) => set("twilio_account_sid", e.target.value)} className="rounded-xl font-mono text-sm" /></div>
          <div className="space-y-1.5"><Label htmlFor="twilio-token">Auth Token {cfg.integrations.twilio.auth_token_hint && <span className="text-[#5C6660] font-normal">(set)</span>}</Label><Input id="twilio-token" type="password" placeholder="leave blank to keep" data-testid="twilio-token" value={form.twilio_auth_token} onChange={(e) => set("twilio_auth_token", e.target.value)} className="rounded-xl font-mono text-sm" /></div>
          <div className="space-y-1.5"><Label htmlFor="twilio-from">From number (E.164)</Label><Input id="twilio-from" data-testid="twilio-from" value={form.twilio_from_number} onChange={(e) => set("twilio_from_number", e.target.value)} className="rounded-xl text-sm" placeholder="+13035551234" /></div>
        </div>
      </div>

      <div className="flex justify-end sticky bottom-0 bg-[#F7F6F2] py-3">
        <Button onClick={save} disabled={saving} data-testid="save-config-btn" className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-11 px-8">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save all settings"}
        </Button>
      </div>
    </div>
  );
}
